/**
 * M9 CH-11: scrapping a claimed ship at a yard, and the no-printer rule (plan
 * `docs/plans/2026-10-05-m9-claimable-hulls.md` §1 H-5 and H-10, §3.3, §4.4; D-570).
 *
 * - **The quote** (`scrapQuote`): `scrap.share × (1 − scrap.perFlaw × flaws) × claim value` (`hulks.json`; the claim
 *   value is the component-tier part of `buildCost` at base price, `flaws.ts claimValueOf`, the kit rule T52; the FR
 *   edge is not a flaw), offered two ways: whole **credits** (rounded down), or the hull's own **claim-cost**
 *   components (`claimCostOf`) at the same factor, rounded down per ware. Rounding down keeps either form at or below
 *   the exact value. A refined or import ware in a build cost (the planned alloy) never comes out of a scrap, and
 *   scrap credits never read the full hull value (that would fail H-5 once the kit drops such a ware).
 * - **The settlement** (`applyScrap`): Career in, Career out. Credits go to the wallet (and `stats.earned`, like a
 *   sale); components go to the hold, refused whole (`cargoFull`) when the hold can't take them. It pays only: the
 *   worker's `scrapClaimedShip` (CH-7a) checks the ship is a registered claimed ship docked at a yard, and CH-7s
 *   removes the hangar entry and calls this once, in the same Career write, after the worker's ok (D-394: settled
 *   exactly once, on the Career).
 * - **No printer** (H-5, the X4 lesson): claim → kit → scrap never nets credits or goods. The kit is
 *   ceil(`kit.share` × each claim-cost ware) plus fuel cells; with `scrap.share` ≤ `kit.share` every scrapped ware
 *   is at most what the kit took, and the credits stay at or below 0.9 × the kit's base value at one flaw (the data
 *   test in `scrap.test.ts` and `survey.test.ts`). Fixing flaws first (CH-10) only adds cost: one fix is ≥ 0.36 ×
 *   hull value, more than any scrap gains from it.
 *
 * Pure; yard time only, never per frame.
 */
import { GOOD_MASS_T, type GoodId, type Goods } from '../career/parts'
import { goodsBlockHoldT, hasHoldMult } from '../career/hullFit'
import { cargoFreeT, EPSILON, withGood, withWallet } from '../career/ops'
import { cargoFull, ok, type CmdResult } from '../career/result'
import type { Career } from '../career/types'
import { hulksConfig, type HulksConfig } from './data'
import { claimCostOf, claimValueOf, flawCount } from './flaws'

/** How a scrapped ship is paid: whole credits, or its own build components to the hold. */
export type ScrapForm = 'credits' | 'goods'

/** What a yard pays for scrapping one claimed ship (both forms; the player picks one). */
export interface ScrapQuote {
  /** Strategic `hulls.json` id. */
  readonly hull: string
  /** Flaws counted (the FR edge is not one). */
  readonly flawCount: number
  /** `share × max(0, 1 − perFlaw × flawCount)`: the share of the claim value paid. */
  readonly factor: number
  /** Whole credits (floor of `factor × claimValueOf(hull)`). */
  readonly credits: number
  /** Whole units per claim-cost ware (floor of `factor × claimCostOf(hull)`); wares that round to 0 are left out. */
  readonly goods: Readonly<Goods>
  /** Tonnes the goods form needs in the hold. */
  readonly goodsT: number
}

/**
 * The scrap quote for a claimed `hull` with the `flaws` bitmask (`OwnedShip.flaws`, the worker's `FleetStore.flaws`;
 * edge bits are ignored). An unknown hull quotes 0 in both forms.
 */
export function scrapQuote(hull: string, flaws: number, cfg: HulksConfig = hulksConfig()): ScrapQuote {
  const n = flawCount(flaws, cfg)
  const factor = cfg.scrap.share * Math.max(0, 1 - cfg.scrap.perFlaw * n)
  const cost = claimCostOf(hull)
  const goods: Goods = {}
  let goodsT = 0
  if (cost !== null) {
    for (const [g, units] of Object.entries(cost) as [GoodId, number | undefined][]) {
      const q = Math.floor(factor * (units ?? 0) + 1e-9)
      if (q <= 0) continue
      goods[g] = q
      goodsT += q * GOOD_MASS_T[g]
    }
  }
  return { hull, flawCount: n, factor, credits: Math.floor(factor * claimValueOf(hull) + 1e-9), goods, goodsT }
}

/**
 * Pays a scrap `quote` to the Career in `form`: credits to the wallet (counted as earned), or the components to the
 * hold (refused whole with `cargoFull` when they don't fit). Pays only; the caller removes the hangar entry in the
 * same write, after the worker's `scrapClaimedShip` succeeded, and never applies a quote twice.
 */
export function applyScrap(career: Career, quote: ScrapQuote, form: ScrapForm): CmdResult<Career> {
  if (form === 'credits') {
    if (quote.credits <= 0) return ok(career)
    const next = withWallet(career, quote.credits)
    return ok({ ...next, stats: { ...next.stats, earned: next.stats.earned + quote.credits } })
  }
  const free = cargoFreeT(career)
  // M10 P5a6b H3: the parts take their class-weighted hold on the flown hull (the quote's raw tonnes on every shipped hull).
  const defId = career.ship.defId
  const needT = hasHoldMult(defId) ? goodsBlockHoldT(defId, quote.goods) : quote.goodsT
  if (needT > free + EPSILON) return cargoFull(needT, free)
  let next = career
  for (const [g, q] of Object.entries(quote.goods) as [GoodId, number | undefined][]) {
    if (q !== undefined && q > 0) next = withGood(next, g, q)
  }
  return ok(next)
}
